import {ChangeDetectionStrategy, Component, Input, OnInit, ViewEncapsulation} from "@angular/core";
import {Clipboard} from "@angular/cdk/clipboard";
import {Modal} from "../../../../../services/modal.service";
import {ToastService} from "src/modules/common/services/toast.service";
import * as moment from "moment";

/**
 * Read-only detail view for a single log row. Renders a structured header +
 * message + a recursive tree for the nested `context`, with a Copy-JSON action
 * and a Pretty/Raw toggle.
 *
 * Works in two modes:
 *  - as a modal (opened via ModalService; reads its row from `modal.params.log`)
 *  - embedded inline via the `[log]` input (e.g. an expanded table row) — in
 *    which case the modal-only heading/close chrome is hidden.
 *
 * Everything is rendered through Angular bindings (auto-escaped) — no
 * bypassSecurityTrustHtml, so arbitrary log payloads can't inject markup.
 */
@Component({
    selector: "admin-logs-view",
    templateUrl: "view.component.html",
    styleUrls: [
        "view.component.scss"
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class LogViewComponent implements OnInit {

    public modal: Modal.IModal;

    /**
     * Log row to display. Bound directly when embedded; populated from the modal
     * params in ngOnInit when opened as a modal.
     */
    @Input()
    public log: any = {};

    public showRaw: boolean = false;

    public constructor(
        private clipboard: Clipboard,
        private toastService: ToastService
    ) {
    }

    public get time(): string {
        return this.log.timestamp
            ? moment(this.log.timestamp).utcOffset(4).format("YYYY-MM-DD HH:mm:ss")
            : "";
    }

    public get hasContext(): boolean {
        return !!this.log.context && Object.keys(this.log.context).length > 0;
    }

    public get rawJson(): string {
        return JSON.stringify(this.log.raw ?? this.log, null, 2);
    }

    public get levelStyle(): { [key: string]: string } {
        switch ((this.log.level || "").toUpperCase()) {
            case "ERROR":
            case "CRITICAL":
            case "ALERT":
            case "EMERGENCY":
                return {color: "#c62828"};
            case "WARNING":
                return {color: "#ef6c00"};
            case "NOTICE":
            case "INFO":
                return {color: "#2e7d32"};
            default:
                return {color: "#757575"};
        }
    }

    public typeOf(value: any): "object" | "array" | "scalar" {
        if (Array.isArray(value)) {
            return "array";
        }
        if (value !== null && typeof value === "object") {
            return "object";
        }
        return "scalar";
    }

    public entries(value: any): { key: string, value: any }[] {
        return Object.keys(value).map((key: string): { key: string, value: any } => ({key, value: value[key]}));
    }

    public display(value: any): string {
        if (value === null || value === undefined) {
            return "null";
        }
        if (typeof value === "boolean") {
            return value ? "true" : "false";
        }
        return String(value);
    }

    public copy(): void {
        this.clipboard.copy(this.rawJson);
        this.toastService.show("Copied to clipboard", "success");
    }

    public close(): void {
        this.modal.response.emit({name: "close"});
    }

    public ngOnInit(): void {
        if (this.modal?.params?.log) {
            this.log = this.modal.params.log;
        }
    }
}
